import moment from "moment";
import { MouseEventHandler, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { PlusIcon } from "../../icons/PlusIcon/PlusIcon";
import { RootState } from "../../store";
import { productSliceActions } from "../../store/products-slice";
import { MinimalProduct } from "../../types/product";
import { MaterialListItem } from "../MaterialListItem/MaterialListItem";
import Modal from "../Modal/Modal";

import euroIcon from "./euro_icon.png";
import styles from "./materials-list.module.css";

type MaterialsListProps = {
  title?: string | null;
  data: MinimalProduct[] | [];
  readOnly?: boolean;
  date?: string;
  plusIcon?: boolean;
  onClick?: MouseEventHandler<HTMLDivElement | HTMLLabelElement>;
};

export const MaterialsList = ({
  title,
  data,
  readOnly,
  date,
  plusIcon = false,
  onClick,
}: MaterialsListProps) => {
  const dispatch = useDispatch();
  const products = useSelector((state: RootState) => state.products);
  const [editMode, setEditMode] = useState(false);
  const [isAddRelatedProductsModalOpen, setIsAddRelatedProductsModalOpen] = useState(false);
  const [relatedProducts, setRelatedProducts] = useState<any[]>();
  const handleEuroClick = async () => {
    setEditMode((prev) => !prev);
  };

  // Only connected materials the technician can pick and has not added yet are suggested.
  const relatedToAdd = useMemo(
    () =>
      (relatedProducts ?? [])
        .map((rel: any) =>
          products.find((p) =>
            rel.isFromSevDesk
              ? p.isSevDeskProduct && String(p.id) === rel.sevdeskId
              : !p.isSevDeskProduct && String(p.id) === rel.id
          )
        )
        .filter((p): p is MinimalProduct => !!p && p.quantity === 0),
    [relatedProducts, products]
  );

  useEffect(() => {
    if (isAddRelatedProductsModalOpen && relatedToAdd.length === 0) {
      setIsAddRelatedProductsModalOpen(false);
    }
  }, [isAddRelatedProductsModalOpen, relatedToAdd]);

  const relatedNames = relatedToAdd.map((p) => p.name).join(", ");

  const addRelatedProducts = () => {
    relatedToAdd.forEach((rel) => {
      dispatch(
        productSliceActions.incrementFor({
          id: rel.id,
          amount: 1,
        })
      );
    });
    setIsAddRelatedProductsModalOpen(false);
  }

  return (
    <div className={styles["materials-list"]}>
      <div className={styles["materials-list__o"]}>
        {title && <h1 className={styles["materials-list__title"]}>{title}</h1>}

        {plusIcon && (
          <div className={styles["plus-icon"]} onClick={onClick}>
            <PlusIcon />
          </div>
        )}

        {!readOnly && (
          <img
            src={euroIcon}
            height="24"
            width="24"
            alt="Euro Icon"
            className={styles["plus-icon"]}
            onClick={handleEuroClick}
          />
        )}
      </div>

      {date && moment(date).isValid() && (
        <div className={styles["additional-text"]}>
          Hinzugefügt am: {moment(date).format("DD.MM.YYYY")}
        </div>
      )}

      <ul>
        {data?.map((product: MinimalProduct, i) => (
          <MaterialListItem
            key={i}
            product={product}
            readOnly={readOnly}
            editMode={editMode}
            openModal={setIsAddRelatedProductsModalOpen}
            setRelatedProducts={setRelatedProducts}
          />
        ))}
      </ul>
      <Modal isOpen={isAddRelatedProductsModalOpen && relatedToAdd.length > 0}
        confirmText="Ja"
        confirmButtonColor="#11A8B8"
        cancelText="Nein"
        message={relatedToAdd.length === 1
          ? `Hierzu liegt ein verknüpftes Material ${relatedNames} vor.`
          : `Hierzu liegen verknüpfte Materialien vor: ${relatedNames}.`}
        note={relatedToAdd.length === 1 ? "Soll dieses hinzugefügt werden?" : "Sollen diese hinzugefügt werden?"}
        noteColor="#8493AB"
        onCancel={() => setIsAddRelatedProductsModalOpen(false)}
        onConfirm={() => addRelatedProducts()}
      />
    </div>
  );
};
